import React, { useEffect, useState } from "react";
import AddJobsModal from "../../Components/JobsItems/AddJobs/AddJobsModal";
import addIcon from "../../../../../Assets/images/add-plus.svg";
import JobTableSection from "../../Components/JobsItems/JobsList/JobsList";
import ViewJobModal from "../../Components/JobsItems/ViewJobModal/ViewJobModal";
import "./Jobs.scss";
import apiClient from "../../../../../Config/apiClient";
import { hasPermission } from "../../Services/permission";
import { jwtDecode } from "jwt-decode";

const jobsBaseUrl = process.env.REACT_APP_TC_JOBS_BASE_PATH;

const JOB_SECTION = "jobs";
const JOB_EDIT_KEY = ["jobs_edit"];

const JobsPage = () => {
  const [startingJobs, setStartingJobs] = useState([]);
  const [progressJobs, setProgressJobs] = useState([]);
  const [allJobs, setAllJobs] = useState([]);
  const [activeTab, setActiveTab] = useState("all");
  const [loading, setLoading] = useState(false);
  const [companyId, setCompanyId] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [userId, setUserId] = useState(null);

  const [jobsPerPage, setJobsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");

  const [filtersData, setFiltersData] = useState({
    starting: {
      job_type: "",
      truck_type: "",
      start_date_from: "",
      start_date_to: "",
      end_date_from: "",
      end_date_to: "",
      posted_date_from: "",
      posted_date_to: "",
    },
    progress: {
      job_type: "",
      truck_type: "",
      start_date_from: "",
      start_date_to: "",
      end_date_from: "",
      end_date_to: "",
      posted_date_from: "",
      posted_date_to: "",
    },
    all: {
      job_type: "",
      truck_type: "",
      start_date_from: "",
      start_date_to: "",
      end_date_from: "",
      end_date_to: "",
      posted_date_from: "",
      posted_date_to: "",
    },
  });

  const [jobData, setJobData] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [refresh, setRefresh] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem("accessToken");
    if (token) {
      const decoded = jwtDecode(token);
      setCompanyId(decoded.company_id);
    }
  }, []);

  useEffect(() => {
    const token = localStorage.getItem("accessToken");
    if (token) {
      const decoded = jwtDecode(token);
      setUserRole(decoded.role);
      setUserId(decoded.user_id);
    }
  }, []);

  const canEditJobs = hasPermission({ section: JOB_SECTION, keys: JOB_EDIT_KEY, });

  useEffect(() => {
    const fetchJobs = async () => {
      try {
        setLoading(true);

        const token = localStorage.getItem("accessToken");
        const decoded = jwtDecode(token);
        const companyId = decoded.company_id;

        const [startingRes, progressRes, allRes] = await Promise.all([
          apiClient.get(`${jobsBaseUrl}/starting-today`, { params: { tc_company_id: companyId } }),
          apiClient.get(`${jobsBaseUrl}/in-progress`, { params: { tc_company_id: companyId } }),
          apiClient.get(`${jobsBaseUrl}/`, { params: { tc_company_id: companyId } }),
        ]);

        setStartingJobs(startingRes.data.data || []);
        setProgressJobs(progressRes.data.data || []);
        setAllJobs(allRes.data.data || []);
      } catch (err) {
        console.error("Error fetching jobs:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchJobs();
  }, [refresh]);

  const getJobsForActiveTab = () => {
    switch (activeTab) {
      case "starting":
        return Array.isArray(startingJobs) ? startingJobs : [];
      case "progress":
        return Array.isArray(progressJobs) ? progressJobs : [];
      case "all":
        return Array.isArray(allJobs) ? allJobs : [];
      default:
        return [];
    }
  };

  const getJobsForRole = (jobs) => {
    if (userRole === "Truck Driver" && userId) {
      return jobs.filter(job => job.assigned_driver_id === userId);
    }
    return jobs;
  };

  const jobsForDisplay = getJobsForRole(getJobsForActiveTab());


  const totalJobs = getJobsForActiveTab().filter((job) =>
    (job.job_name && job.job_name.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (job.job_description && job.job_description.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (job.job_type && job.job_type.toLowerCase().includes(searchTerm.toLowerCase()))
  ).length;
  const startIndex = (currentPage - 1) * jobsPerPage;

  const handleFilterChange = async (newFilters) => {
    setFiltersData((prev) => ({
      ...prev,
      [activeTab]: newFilters,
    }));
    setCurrentPage(1);

    try {
      setLoading(true);

      let apiUrl = "";
      switch (activeTab) {
        case "starting":
          apiUrl = `${jobsBaseUrl}/starting-today`;
          break;
        case "progress":
          apiUrl = `${jobsBaseUrl}/in-progress`;
          break;
        case "all":
          apiUrl = `${jobsBaseUrl}/`;
          break;
      }

      const response = await apiClient.get(apiUrl, {
        params: { ...newFilters, tc_company_id: companyId },
      });

      if (activeTab === "starting") setStartingJobs(response.data.data || []);
      if (activeTab === "progress") setProgressJobs(response.data.data || []);
      if (activeTab === "all") setAllJobs(response.data.data || []);
    } catch (err) {
      console.error("Error applying filters:", err);
    } finally {
      setLoading(false);
    }
  };

  const resetFilters = async () => {
    const defaultFilters = {
      job_type: "",
      truck_type: "",
      start_date_from: "",
      start_date_to: "",
      end_date_from: "",
      end_date_to: "",
      posted_date_from: "",
      posted_date_to: "",
    };

    setFiltersData((prev) => ({
      ...prev,
      [activeTab]: defaultFilters,
    }));

    setCurrentPage(1);

    try {
      setLoading(true);

      let apiUrl = "";
      switch (activeTab) {
        case "starting":
          apiUrl = `${jobsBaseUrl}/starting-today`;
          break;
        case "progress":
          apiUrl = `${jobsBaseUrl}/in-progress`;
          break;
        case "all":
          apiUrl = `${jobsBaseUrl}/`;
          break;
      }

      const response = await apiClient.get(apiUrl, {
        params: { ...defaultFilters, tc_company_id: companyId },
      });

      if (activeTab === "starting") setStartingJobs(response.data.data || []);
      if (activeTab === "progress") setProgressJobs(response.data.data || []);
      if (activeTab === "all") setAllJobs(response.data.data || []);
    } catch (err) {
      console.error("Error resetting filters:", err);
    } finally {
      setLoading(false);
    }
  };
  const handleStatusChange = (newStatus) => {
    setRefresh(prev => !prev);
  };

  return (
    <main className="full-wraper">
      <div className="main-wraper">
        <div className="container-fluid">
          <div className="main-inner">
            <div className="common-top">
              <div className="ct-tabs">
                <ul className="nav nav-tabs jtabs" role="tablist">

                  <li className="nav-item">
                    <button
                      className={`nav-link ctt-button ${activeTab === "all" ? "active" : ""}`}
                      onClick={() => {
                        setActiveTab("all");
                        setCurrentPage(1);
                      }}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="21.739" height="21.739" viewBox="0 0 21.739 21.739" fill="none">
                        <g transform="translate(-1.15 -1.15)">
                          <path id="stroke-change" d="M12.02,22.039A10.02,10.02,0,1,0,2,12.02,10.02,10.02,0,0,0,12.02,22.039Z" stroke="#111" strokeWidth="1.7" />
                          <path id="stroke-change" d="M16.6,9.672A5.078,5.078,0,0,0,12,7a4.834,4.834,0,0,0-5,4.008" stroke="#111" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
                          <path id="stroke-change" d="M14.508,9.726h1.91a.6.6,0,0,0,.6-.6V7.5m-9.6,6.179a5.037,5.037,0,0,0,4.6,3.34,5.251,5.251,0,0,0,5-5.01" stroke="#111" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
                          <path id="stroke-change" d="M9.511,13.622H7.6a.6.6,0,0,0-.6.6v2.182" stroke="#111" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
                        </g>
                      </svg>
                      <span>All Jobs</span>
                    </button>
                  </li>

                  <li className="nav-item">
                    <button
                      className={`nav-link ctt-button ${activeTab === "starting" ? "active" : ""}`}
                      onClick={() => {
                        setActiveTab("starting");
                        setCurrentPage(1);
                      }}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="21" height="21" viewBox="0 0 24 24" fill="none">
                        <path id="fill-change" d="M12,1.5A10.5,10.5,0,1,0,22.5,12,10.512,10.512,0,0,0,12,1.5Zm0,1.615A8.885,8.885,0,1,1,3.115,12,8.872,8.872,0,0,1,12,3.115Zm.582,3.711L11.418,7.962l3.231,3.231h-8.3v1.615h8.3l-3.231,3.231,1.163,1.136,4.593-4.593L17.756,12l-.582-.582L12.581,6.825Z" transform="translate(-1.5 -1.5)" fill="#111" />
                      </svg>
                      <span>Starting Today</span>
                    </button>
                  </li>

                  <li className="nav-item">
                    <button
                      className={`nav-link ctt-button ${activeTab === "progress" ? "active" : ""}`}
                      onClick={() => {
                        setActiveTab("progress");
                        setCurrentPage(1);
                      }}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="23" height="23" viewBox="0 0 23 23" fill="none">
                        <path id="stroke-change" d="M13.5,6.5V3m0,21V21.667M21.667,13.5H24m-21,0H6.5M20.1,6.9l.825-.825M6.075,20.925l1.65-1.65m11.55,0,1.65,1.65M6.075,6.075,8.55,8.55" transform="translate(-2 -2)" stroke="#111" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                      <span>In Progress</span>
                    </button>
                  </li>

                </ul>
              </div>
            </div>

            <JobTableSection
              jobs={jobsForDisplay}
              jobsPerPage={jobsPerPage}
              currentPage={currentPage}
              onPageChange={setCurrentPage}
              setJobsPerPage={setJobsPerPage}
              totalJobs={totalJobs}
              startIndex={startIndex}
              onSearchChange={setSearchTerm}
              searchTerm={searchTerm}
              setJobData={setJobData}
              setShowModal={setShowModal}
              filters={filtersData[activeTab]}
              onFilterChange={handleFilterChange}
              resetFilters={resetFilters}
              loading={loading}
              setRefresh={setRefresh}
              canEditJobs={canEditJobs}
            />
          </div>
        </div>
      </div>

      <AddJobsModal jobData={jobData} showModal={showModal} setShowModal={setShowModal} setRefresh={setRefresh} />
      <ViewJobModal jobId={jobData?.id} refresh={() => setRefresh(prev => !prev)} onStatusChange={handleStatusChange} />
    </main>
  );
};

export default JobsPage;